import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import {Modal, Pagination, Popover, Table} from 'antd';
import { withWindowSize } from 'air-with-window-size';
import {CloseIcon, InfoCircleIcon} from '../../../../../common/icons';

import './VehiclesTable.css';
import { api } from '../../../../../services';
import download from 'downloadjs';
import AirButton from "../../../../../common/components/AirButton";

const pageSizeDefault = 7;
const pageSizeAlternative = 5;

const DisconnectionRequestConfirmModal = ({titleText, bodyText, buttonText, onConfirmFunction, visible, onCancel}) => (
  <Modal className={'DisconnectionRequestConfirmModal'} visible={visible} onCancel={onCancel} footer={null} closable={false} destroyOnClose={true} width={600}>
    <div className="DisconnectionRequestConfirmModalAction" onClick={onCancel} style={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}>
      <CloseIcon style={{ width: 10, height: 10 }} />
    </div>
    <div className={'DisconnectionRequestConfirmModalContent'}>
      <div className={'DisconnectionRequestConfirmModalTitle'}>{titleText}</div>
      <div className={'DisconnectionRequestConfirmModalBody'}>{bodyText}</div>
      <div className={'DisconnectionRequestConfirmModalFooterContainer'}>
        <AirButton
          light="true"
          size="large"
          onClick={onConfirmFunction}
          label={buttonText}
          bgcolor={`var(--primary-color)`}
        />
      </div>
    </div>
  </Modal>
);

@inject('commonStore', 'translationsStore', 'fleetCustomerContractsStore')
@observer
class VehiclesTable extends Component {
  constructor(props) {
    super(props);
    this.defaultPageSize = 1;
    const dataSource = this.buildDataSourceWithKeys(this.props.vehicles);
    const pageSize = this.defaultPageSize;

    let pagination = false;
    let paginationText = '';
    this.state = {
      dataSourceBuffer: dataSource,
      selectedRowKeys: [],
      pageSize,
      pagination,
      paginationText
    };
  }

  componentDidMount = () => {
    const dataSource = this.buildDataSourceWithKeys(this.props.vehicles);
    if (!isEqual(dataSource, this.state.dataSourceBuffer) || (dataSource.length > 0 && !this.state.pagination)) {
      this.buildPagination();
    }
  };

  componentDidUpdate(prevProps) {
    const someSizeChanged = prevProps.componentHeight !== this.props.componentHeight || prevProps.windowHeight !== this.props.windowHeight;
    const dataSource = this.buildDataSourceWithKeys(this.props.vehicles);
    if (someSizeChanged || !isEqual(dataSource, this.state.dataSourceBuffer) || (dataSource.length > 0 && !this.state.pagination)) {
      this.buildPagination();
    }
  }

  helperPageSize = () => {
    const defaultDocHeight = 800;
    if (this.props.documentHeight === defaultDocHeight) {
      return !this.props.group ? pageSizeDefault : pageSizeAlternative;
    } else {
      return !this.props.group
        ? Math.floor((this.props.documentHeight * pageSizeDefault) / defaultDocHeight)
        : Math.floor((this.props.documentHeight * pageSizeAlternative) / defaultDocHeight);
    }
  };

  changePageSize = pageSize => {
    this.setState({ pageSize });
  };

  helperPaginationText = (start, end, length) => {
    const { translationsStore } = this.props;
    if (length === 0) return `0 ${translationsStore.translate('vehicles')}`;
    else if (length === 1) return `1 ${translationsStore.translate('vehicle')}`;
    else return `${start}-${end} ${translationsStore.translate('of')} ${length} ${translationsStore.translate('vehicles')}`;
  };

  buildDataSourceWithKeys = collection => collection.map((item, index) => ({ ...item, key: index }));

  buildSelectedRowKeys = (dataSource, selectedRows) => {
    let selectedRowKeys = [];
    dataSource.forEach((row, index) => {
      if (typeof selectedRows.find(selected => selected.uuid === row.uuid) !== 'undefined') selectedRowKeys.push(index);
    });
    return selectedRowKeys;
  };

  toggleSelectAllRows = () => {
    const { vehicles, onSelectedRowsChange } = this.props;
    let { selectedRows } = this.props;

    const dataSource = this.buildDataSourceWithKeys(vehicles);
    let selectedRowKeys = this.buildSelectedRowKeys(dataSource, selectedRows);

    if (selectedRowKeys.length > 0 && selectedRowKeys.length === dataSource.length) {
      selectedRows = [];
    } else {
      selectedRows = dataSource;
    }
    selectedRowKeys = selectedRows.map(row => row.key);
    onSelectedRowsChange(selectedRows);
    this.setState({ selectedRowKeys });
  };

  toggleSelectRow = data => {
    const { vehicles, onSelectedRowsChange } = this.props;
    let { selectedRows } = this.props;

    const foundIndex = selectedRows.findIndex(selected => selected.vehicle.uuid === data.vehicle.uuid);
    if (foundIndex === -1) {
      selectedRows.push(data);
    } else {
      selectedRows.splice(foundIndex, 1);
    }
    onSelectedRowsChange(selectedRows);

    const dataSource = this.buildDataSourceWithKeys(vehicles);
    const selectedRowKeys = this.buildSelectedRowKeys(dataSource, selectedRows);
    this.setState({ selectedRowKeys });
  };

  buildPagination = () => {
    const dataSource = this.buildDataSourceWithKeys(this.props.vehicles);
    const pageSize = this.helperPageSize();
    const pagination = {
      total: dataSource.length,
      defaultCurrent: 1,
      current: 1,
      // hideOnSinglePage: true,
      pageSize,
      simple: true,
      size: 'small',
      onChange: this.onPageChange
    };
    const start = 1;
    const end = Math.min(pagination.pageSize, pagination.total);

    const paginationText = this.helperPaginationText(start, end, dataSource.length);
    this.setState({
      dataSourceBuffer: dataSource,
      pageSize,
      pagination: { ...pagination },
      paginationText
    });
  };

  onPageChange = (page, pageSize) => {
    const dataSource = this.buildDataSourceWithKeys(this.props.vehicles);
    const { pagination } = this.state;
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, pagination.total);
    let paginationText = this.helperPaginationText(start, end, dataSource.length);
    this.setState({
      dataSourceBuffer: dataSource,
      pagination: { ...pagination, current: page },
      paginationText
    });
  };

  downloadDocument = (plate, documentUrl) => {
    if (documentUrl) {
      api('downloadDocument', documentUrl).then(response => {
        download(response, `collaudo_${plate}.pdf`, 'application/pdf');
      });
    }
  };

  createDisconnection = record => {
    this.closeDisconnectionModal();

    const { fleetCustomerContractsStore } = this.props;
    return fleetCustomerContractsStore.createDisconnection({ uuid: record.uuid });
  };

  removeDisconnection = record => {
    this.closeDisconnectionModal();

    const { fleetCustomerContractsStore } = this.props;
    return fleetCustomerContractsStore.removeDisconnection({ uuid: record.uuid });
  };

  closeDisconnectionModal = () => {
    this.setState({isVisibleDisconnectionRequestConfirmModal: false, disconnectionModalProps: undefined});
  };

  render() {
    const { commonStore, translationsStore, selectedRows, vehicles } = this.props;

    const dataSource = this.buildDataSourceWithKeys(vehicles);
    const selectedRowKeys = this.buildSelectedRowKeys(dataSource, selectedRows);

    let checkmarkHeaderClassName = 'CheckmarkHeader';
    if (selectedRowKeys.length > 0 && selectedRowKeys.length === dataSource.length) {
      checkmarkHeaderClassName = `${checkmarkHeaderClassName} CheckmarkHeaderSelected`;
    } else if (selectedRowKeys.length > 0) {
      checkmarkHeaderClassName = `${checkmarkHeaderClassName} CheckmarkHeaderIndeterminate`;
    }

    const columns = [
      {
        key: 'checkmark',
        dataIndex: 'checkmark',
        title: (
          <div className={checkmarkHeaderClassName} onClick={this.toggleSelectAllRows}>
            <span className="checkmark" />
          </div>
        ),
        render: (text, record) => {
          const checked = selectedRowKeys.findIndex(element => element === record.key) !== -1;
          return (
            <div className={`Checkmark${checked ? ' CheckmarkSelected' : ''}`} onClick={() => this.toggleSelectRow(record)}>
              <span className="checkmark" />
            </div>
          );
        }
      },
      {
        key: 'plate',
        dataIndex: 'plate',
        className: 'ColumnCentered',
        title: translationsStore.translate('vehiclesManager.vehicleTable.headerLabels.plate'),
        render: (text, record) => (record.activeVehicle ? record.activeVehicle.license_plate : '-')
      },
      {
        key: 'make',
        dataIndex: 'make',
        title: translationsStore.translate('vehiclesManager.vehicleTable.headerLabels.make'),
        render: (text, record) => (record.activeVehicle ? record.activeVehicle.brand.name : '-')
      },
      {
        key: 'model',
        dataIndex: 'model',
        title: translationsStore.translate('vehiclesManager.vehicleTable.headerLabels.model'),
        render: (text, record) => (record.activeVehicle ? record.activeVehicle.model.name : '-')
      },
      {
        key: 'status',
        dataIndex: 'status',
        className: 'ColumnWithIcon',
        title: translationsStore.translate('vehiclesManager.vehicleTable.headerLabels.status'),
        render: (text, record) => {
          if (record.hasOwnProperty('vehicleStatusHistory') && record.vehicleStatusHistory.length > 0) {
            let { vehicleStatusHistory } = record;
            vehicleStatusHistory = vehicleStatusHistory.slice().sort((a, b) => (a.statusDate > b.statusDate ? 1 : b.statusDate > a.statusDate ? -1 : 0));
            const columns = [
              {
                key: 'status',
                dataIndex: 'status',
                title: translationsStore.translate('vehiclesManager.vehicleTable.vehicleStatusHistory.columns.status'),
                render: (text, record) => translationsStore.translate(`vehiclesManager.status.${record.status}`)
              },
              {
                key: 'statusDate',
                dataIndex: 'statusDate',
                title: translationsStore.translate('vehiclesManager.vehicleTable.vehicleStatusHistory.columns.statusDate'),
                render: (text, record) => new Date(record.statusDate).toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' })
              }
            ];
            const dataSource = vehicleStatusHistory.map((data, index) => ({ ...data, key: index }));
            return (
              <div>
                <Popover placement={'left'} content={<Table className="VehicleStatusHistoryFrame" columns={columns} dataSource={dataSource} pagination={false} />}>
                  <InfoCircleIcon style={{ width: '20px', height: '20px', color: 'var(--primary-color)', backgroundColor: 'white', borderRadius: 20 }} />
                </Popover>
                <span>{translationsStore.translate(`vehiclesManager.status.${record.managementStatus}`)}</span>
              </div>
            );
          } else {
            return '-';
          }
        }
      },
      {
        key: 'statusDate',
        dataIndex: 'statusDate',
        title: translationsStore.translate('vehiclesManager.vehicleTable.headerLabels.statusDate'),
        render: (text, record) => {
          if (record.hasOwnProperty('vehicleStatusHistory') && record.vehicleStatusHistory.length > 0) {
            return new Date(record.managementStatusDate).toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
          } else {
            return '-';
          }
        },
        sorter: (a, b) => new Date(a.managementStatusDate) < new Date(b.managementStatusDate) ? -1 : 1,
        sortDirections: ['descend', 'ascend']
      },
      {
        key: 'product',
        dataIndex: 'product',
        className: 'ColumnCentered',
        title: translationsStore.translate('vehiclesManager.vehicleTable.headerLabels.product'),
        render: (text, record) => (!!record.products && !!record.products[0].name ? record.products.map(product => product.name).join(', ') : '-')
      },
      {
        key: 'actions',
        dataIndex: 'actions',
        title: translationsStore.translate('vehiclesManager.vehicleTable.headerLabels.actions'),
        render: (text, record) => {
          const documentUrl = record.activeInstallation && record.activeInstallation.node && record.activeInstallation.node.installation_document_url;

          const clsDownloadDocument = classNames({
            button: true,
            disabled: !documentUrl
          });

          return (
            <div style={{display: 'flex', justifyContent: 'center'}}>
              <div style={{width: '45px'}} className={clsDownloadDocument} onClick={() => this.downloadDocument(record.activeVehicle.license_plate, documentUrl)}>
                {translationsStore.translate('vehiclesManager.vehicleTable.actions.labelDownloadDocument')}
              </div>
            </div>
          );
        }
      },
      {
        render: (text, record) => {
          const clsDisconnection = classNames({
            button: true,
            disconnection: true
          });

          return (
            <Fragment>
              {(record.activeInstallation && record.managementStatus !== 'disconnected') &&
              (record.activeInstallation.node.disconnection_request_date ? (
                <div className={clsDisconnection} onClick={() => this.setState({
                  isVisibleDisconnectionRequestConfirmModal: true,
                  disconnectionModalProps: {
                    record,
                    titleText: <div>
                        {translationsStore.translate('vehiclesManager.vehicleTable.disconnectionModal.confirmYouWantTo')}
                        <b>{translationsStore.translate('vehiclesManager.vehicleTable.disconnectionModal.disableCancelTheDisconnection')}</b>
                        {translationsStore.translate('vehiclesManager.vehicleTable.disconnectionModal.ofTheVehicle')}
                        {record.activeVehicle ? record.activeVehicle.license_plate : ''}?
                      </div>,
                    onConfirmFunction: () => this.removeDisconnection(record)
                  }
                })}>
                  {translationsStore.translate('vehiclesManager.vehicleTable.actions.labelRemoveDisconnection')}
                </div>
              ) : (
                <div className={clsDisconnection} onClick={() => this.setState({
                  isVisibleDisconnectionRequestConfirmModal: true,
                  disconnectionModalProps: {
                    record,
                    titleText: <div>
                        {translationsStore.translate('vehiclesManager.vehicleTable.disconnectionModal.confirmYouWantTo')}
                        <b>{translationsStore.translate('vehiclesManager.vehicleTable.disconnectionModal.disableTheConnection')}</b>
                        {translationsStore.translate('vehiclesManager.vehicleTable.disconnectionModal.onTheVehicle')}
                        {record.activeVehicle ? record.activeVehicle.license_plate : ''}?
                      </div>,
                    bodyText: translationsStore.translate('vehiclesManager.vehicleTable.disconnectionModal.createDisconnectionBody'),
                    onConfirmFunction: () => this.createDisconnection(record)
                  }
                })}>
                  {translationsStore.translate('vehiclesManager.vehicleTable.actions.labelCreateDisconnection')}
                </div>
              ))}
            </Fragment>
          );
        }
      }
    ];

    return (
      <div className="VehiclesTable">
        <Table columns={columns} dataSource={dataSource} pagination={this.state.pagination} rowClassName="ClientRow" />
        {!commonStore.loading && (
          <div className="VehiclesTablePagination">
            <div>{this.state.paginationText}</div>
            {dataSource.length > this.helperPageSize() && <Pagination {...this.state.pagination} />}
          </div>
        )}
        {this.state.disconnectionModalProps && <DisconnectionRequestConfirmModal
          visible={this.state.isVisibleDisconnectionRequestConfirmModal}
          titleText={this.state.disconnectionModalProps.titleText}
          bodyText={this.state.disconnectionModalProps.bodyText}
          buttonText={translationsStore.translate('vehiclesManager.vehicleTable.disconnectionModal.confirm')}
          onConfirmFunction={this.state.disconnectionModalProps.onConfirmFunction}
          onCancel={() => this.closeDisconnectionModal()}
        />}
      </div>
    );
  }
}

export default withWindowSize(VehiclesTable);



// WEBPACK FOOTER //
// ./src/components/App/VehiclesManagerDashboard/SelectableVehicles/VehiclesTable/VehiclesTable.js